import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

type Options = {
  /** Throttle window. Subsequent schedule() calls within this window collapse
   *  into a single refetch on the trailing edge. Defaults to 400ms. */
  throttleMs?: number;
  /** Whether to drop prefetched (`type: 'inactive'`) pages alongside the
   *  refetch. Useful for paginated queries where stale prefetched pages can
   *  shadow fresh data on the next page navigation. Defaults to true. */
  dropInactivePages?: boolean;
};

/**
 * Returns a `schedule` callback that calls `refetchQueries({ queryKey, type:
 * 'active' })` at most once per `throttleMs`. Optionally also runs
 * `removeQueries({ type: 'inactive' })` so prefetched pages aren't stale.
 *
 * Built for socket-driven refetches under burst conditions (Firecrawl can
 * fire 100+ webhooks in <5s). `refetchQueries` (not `invalidateQueries`)
 * matters: the global default sets `staleTime: Infinity` and TanStack Query
 * coalesces rapid invalidations such that most never reach the network.
 *
 * Cleans up its pending timer on unmount.
 */
export const useThrottledRefetch = (
  queryKey: QueryKey,
  { throttleMs = 400, dropInactivePages = true }: Options = {},
) => {
  const queryClient = useQueryClient();
  const timerRef = useRef<number | null>(null);
  // Lazy-stable key reference so the schedule callback identity stays stable
  // even when callers pass an inline array literal (which would otherwise
  // re-create the callback every render and re-bind socket listeners).
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  const schedule = useCallback(() => {
    if (timerRef.current !== null) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void queryClient.refetchQueries({ queryKey: queryKeyRef.current, type: 'active' });
      if (dropInactivePages) {
        queryClient.removeQueries({ queryKey: queryKeyRef.current, type: 'inactive' });
      }
    }, throttleMs);
  }, [queryClient, throttleMs, dropInactivePages]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  return schedule;
};
